import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  removePlusFromPhoneNumber,
  addPlusToPhoneNumber,
  parsePhoneNumber,
} from 'features/call/call-base/helpers';
import { CallDaoService } from 'features/call/call-base/services';
import {
  syncHearingPhoneExtensionToCallRecord,
  updateCalleeConnectionString,
} from 'features/call/call-base/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import {
  disconnectHearingByIdForUI,
  setCallDirection,
  setHearingLocation,
  setHearingPhoneExtension,
  setPrimaryHearingPhoneNumber,
} from 'features/call/call-hearing/store';
import {
  dialedNumbersListSelector,
  setDialedNumberHistory,
} from 'features/dialed-numbers-history/store';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { isSpecialPhoneNumber } from 'features/n11/helpers';
import {
  useHearingConnect,
  useHearingDisconnect,
  useVoiceMeetingSessionInit,
} from 'features/voice-meeting/hooks';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import { agentDisconnectHearing } from 'features/reporting/store';
import { spawnedCallCalleeNumberResolved } from 'features/spawn-call/store';

export const useCallHearingParticipantHandlers = () => {
  const dispatch = useAppDispatch();
  const { connectHearing } = useHearingConnect();
  const { disconnectHearingFromChimeMeeting } = useHearingDisconnect();
  const { replaceN11Number } = useVoiceMeetingSessionInit();
  const callId = useAppSelector(callIdSelector);
  const dialedNumbersList = useAppSelector(dialedNumbersListSelector);
  const isVrs = useAppSelector(isVrsModeSelector);

  const handleRegularHearingCall = useCallback(
    async (phoneNumber: string) => {
      dispatch(setCallDirection('outbound'));
      dispatch(
        setDialedNumberHistory([
          removePlusFromPhoneNumber(phoneNumber),
          ...dialedNumbersList,
        ])
      );

      void dispatch(syncHearingPhoneExtensionToCallRecord(phoneNumber));
      await connectHearing(phoneNumber);
    },
    [connectHearing, dialedNumbersList, dispatch]
  );

  const handleHearingSpawnNewCall = useCallback(
    async (phoneNumber: string, phoneExtension = '') => {
      let processedNumber = phoneNumber;

      if (isVrs && isSpecialPhoneNumber(phoneNumber)) {
        processedNumber = (await replaceN11Number(phoneNumber)) ?? phoneNumber;
      }

      dispatch(setPrimaryHearingPhoneNumber(processedNumber));
      dispatch(
        setHearingPhoneExtension({
          phoneNumber: processedNumber,
          phoneExtension,
        })
      );

      const updatedCall = await CallDaoService.updateCalleePhoneNumber({
        callId,
        phoneNumber: addPlusToPhoneNumber(parsePhoneNumber(processedNumber)),
      });
      const { CalleeConnectionLocation, CalleeConnectionString } = updatedCall;

      dispatch(updateCalleeConnectionString(CalleeConnectionString));
      dispatch(spawnedCallCalleeNumberResolved(updatedCall));

      dispatch(
        setHearingLocation({
          location: CalleeConnectionLocation,
          phoneNumber: processedNumber,
        })
      );

      await handleRegularHearingCall(processedNumber);
    },
    [callId, dispatch, handleRegularHearingCall, isVrs, replaceN11Number]
  );

  const handleHearingDisconnect = async (id: string) => {
    try {
      // this order is important, we need to mark the agent as the one that disconnected the hearing
      // before disconnecting from the voice meeting
      dispatch(agentDisconnectHearing());
      await disconnectHearingFromChimeMeeting(id);
    } catch (error) {
      VoiceMeetingEventBus.finishAndDeleteVoiceSession();
      sendAnalyticsInfo({
        Method: 'handleHearingDisconnect',
        Message:
          'Error disconnecting hearing from voice meeting, Chime meeting was completed',
        Error: error,
      });
      await dispatch(disconnectHearingByIdForUI(id));
    }
  };

  return {
    handleRegularHearingCall,
    handleHearingDisconnect,
    handleHearingSpawnNewCall,
  };
};
